import { useImperativeHandle, forwardRef, useEffect } from "react";
import { Box, Grid2, Typography } from "@mui/material";
import { SubmitHandler, useForm } from "react-hook-form";
import { useSkeletonLoader } from "../../../hooks/useSkeletonLoader";
import NotificationSettings from "../../../types/notificationSettings";
import FormRadio from "../../forms/FormRadio";
import FormAutocomplete from "../../forms/FormAutocomplete";
import { useDispatch, useSelector } from "../../../store";
import { getIncidentTypes, getJobTypes } from "../../../slices/settings";
import Option from "../../../types/option";
import { Applications } from "../../../types/company";
import { wrapWithTooltip } from "../../../helpers/wrapWithTooltip";
import { checkLicense } from "../../../helpers/checkLicense";

interface FormProps {
  onSave: SubmitHandler<NotificationSettings>;
}

export type SvFormSubmitHandler = () => void;

const SvNotifications = forwardRef<SvFormSubmitHandler, FormProps>(
  (props, ref) => {
    const { onSave } = props;

    const dispatch = useDispatch();
    const { incidentTypes, jobTypes, settings } = useSelector(
      (store) => store.settings,
    );
    const { userCompany } = useSelector((store) => store.auth);

    const getSelectedOptions = (selected: Option[], fullOptions: Option[]) => {
      return selected.map((o) => {
        const fullSelected = fullOptions.find((i) => i.id === o.id);
        return {
          id: o.id,
          value: fullSelected?.value,
        };
      });
    };

    const { handleSubmit, control, watch, setValue } =
      useForm<NotificationSettings>({
        mode: "onChange",
        defaultValues: settings?.notificationSettings,
      });

    const formValues = watch();

    const { loadWithSkeleton } = useSkeletonLoader(false);

    useImperativeHandle(ref, () => handleSubmit(onSave));

    useEffect(() => {
      if (checkLicense(Applications.Responder, userCompany?.licenses || []))
        dispatch(getIncidentTypes());
      if (checkLicense(Applications.Gmd, userCompany?.licenses || []))
        dispatch(getJobTypes());
    }, [dispatch, userCompany?.licenses]);

    return (
      <Box display="flex" flexDirection="column" width="100%" height="100%">
        {(!checkLicense(Applications.Responder, userCompany?.licenses || []) ||
          !checkLicense(Applications.Gmd, userCompany?.licenses || [])) && (
          <Box
            display="flex"
            flexDirection="row"
            width="100%"
            justifyContent="center"
            marginBottom={2}
          >
            <Typography color="error" variant="caption">
              Make sure your account is fully set up in other sites before
              adding integrations to Detector. To enable available integrations,
              contact your SVPro admin!
            </Typography>
          </Box>
        )}
        <Box display="flex" flexDirection="row" width="100%">
          <form>
            <Grid2 container spacing={2} marginBottom={3}>
              {wrapWithTooltip(
                !checkLicense(Applications.Gmd, userCompany?.licenses || []),
                "Update GMD license to enable this feature.",
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  {loadWithSkeleton(
                    <FormRadio
                      label="Enable job creation in Getting More Done?"
                      control={control}
                      fieldName="enableGmd"
                      onChange={(e) => setValue("enableGmd", e)}
                      disabled={
                        !checkLicense(
                          Applications.Gmd,
                          userCompany?.licenses || [],
                        ) || !jobTypes.length
                      }
                    />,
                  )}
                </Grid2>,
              )}
              <Grid2 size={{ xs: 12, sm: 6 }}>
                {loadWithSkeleton(
                  <FormAutocomplete
                    control={control}
                    fieldName="gmdTicketTypes"
                    options={jobTypes}
                    multiple
                    label={`Enabled GMD Job Types`}
                    defaultValue={formValues.gmdTicketTypes || []}
                    disabled={
                      !checkLicense(
                        Applications.Gmd,
                        userCompany?.licenses || [],
                      ) || !formValues.enableGmd
                    }
                    required={formValues.enableGmd}
                  />,
                )}
              </Grid2>
              {wrapWithTooltip(
                !checkLicense(
                  Applications.Responder,
                  userCompany?.licenses || [],
                ),
                "Update responder license to enable this feature.",
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  {loadWithSkeleton(
                    <FormRadio
                      label="Enable incident creation in Responder?"
                      control={control}
                      fieldName="enableResponder"
                      onChange={(e) => setValue("enableResponder", e)}
                      disabled={
                        !checkLicense(
                          Applications.Responder,
                          userCompany?.licenses || [],
                        ) || !incidentTypes.length
                      }
                    />,
                  )}
                </Grid2>,
              )}
              <Grid2 size={{ xs: 12, sm: 6 }}>
                {loadWithSkeleton(
                  <FormAutocomplete
                    control={control}
                    fieldName="responderTicketTypes"
                    options={incidentTypes || []}
                    multiple
                    label={`Enabled Incident Types`}
                    defaultValue={
                      getSelectedOptions(
                        formValues.responderTicketTypes!,
                        incidentTypes,
                      ) || []
                    }
                    disabled={
                      !checkLicense(
                        Applications.Responder,
                        userCompany?.licenses || [],
                      ) || !formValues.enableResponder
                    }
                    staticFilter
                    required={formValues.enableResponder}
                  />,
                )}
              </Grid2>
              {wrapWithTooltip(
                !checkLicense(
                  Applications.Investigator,
                  userCompany?.licenses || [],
                ),
                "Coming Soon!",
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  {loadWithSkeleton(
                    <FormRadio
                      label="Enable case creation in Investigator?"
                      control={control}
                      fieldName="enableInvestigator"
                      disabled={
                        !checkLicense(
                          Applications.Investigator,
                          userCompany?.licenses || [],
                        )
                      }
                      onChange={(e) => setValue("enableInvestigator", e)}
                    />,
                  )}
                </Grid2>,
              )}
            </Grid2>
          </form>
        </Box>
      </Box>
    );
  },
);

export default SvNotifications;
